<script lang="ts">
	import Basic from '$lib/templates/Basic.svelte'
	import { fetchUtils } from '$utils/fetchUtils'
	import { onMount, setContext } from 'svelte'
	import type { Tag } from '$types/tag'
	import { DefaultNotification, NotificationType } from '$types/notification'
	import { addNotification } from '$lib/stores'
	import Permission from '$lib/menubar/Permission.svelte'
	import { InputIcons, TooltipPlacements, UserPermission } from '$types/enums'
	import InputField from '$lib/molecules/InputField.svelte'
	import Button from '$lib/atoms/Button.svelte'
	import IconAnnotation from '$svg/icon-annotation.svg?component'
	import IconPlus from '$svg/icon-plus.svg?component'
	import IconEdit from '$svg/icon-edit.svg?component'
	import IconTrash from '$svg/icon-trash.svg?component'
	import EditTagDialog from '$lib/manage/tags/EditTagDialog.svelte'
	import Tooltip_v5 from '$lib/atoms/Tooltip_v5.svelte'
	import DeleteTagDialog from '$lib/manage/tags/DeleteTagDialog.svelte'
	import { t } from '$i18n/i18n'

	setContext('useMaterialDesign', true)

	let globalTags: Tag[] = $state([])
	let searchTerm = $state('')

	let editDialog: EditTagDialog
	let deleteDialog: DeleteTagDialog

	const filteredTags = $derived.by(() =>
		globalTags.filter((tag) => tag.label.toLowerCase().includes(searchTerm.toLowerCase()))
	)

	/**
	 * this call IS the page, so it owns the page outcome. passing `false` made a
	 * missing permission a toast on an empty table: measured logged in without
	 * the permission, /api/tags/global answers 403 {"errorMessage":"Forbidden"},
	 * while the deployed front door sends the same account to /401. publishing it
	 * lets doHandle's 403 branch render the message instead
	 */
	function fetchAllTags() {
		fetchUtils
			.get('/api/tags/global')
			.then((res) => (globalTags = res))
			.catch((err) => {
				/**
				 * doHandle already painted the page for the errors it owns; adding a
				 * toast on top renders one error state over another (fetchUtils.ts:20-27)
				 */
				if (err?.handled) return

				const notif = DefaultNotification(
					$t('UI.tags.notification.load.error', { error: err.message }),
					NotificationType.ERROR
				)
				addNotification(notif)
			})
	}

	function openEditDialog(tag: Tag | undefined = undefined) {
		const mode = tag ? 'edit' : 'create'
		const onSave = tag ? (label: string) => saveTag(label, tag.publicId) : saveTag
		editDialog.open(mode, onSave, tag?.label)
	}

	function openDeleteDialog(tag: Tag) {
		deleteDialog.open(() => deleteTag(tag.publicId), tag.label)
	}

	function saveTag(newTagLabel: string, tagPublicId: string | undefined = undefined) {
		fetchUtils
			.post('/api/tags/global', { label: newTagLabel, tagPublicId }, false)
			.then(() => {
				const msgText = tagPublicId
					? $t('UI.tags.notification.edit.success')
					: $t('UI.tags.notification.create.success')
				const notif = DefaultNotification(msgText, NotificationType.SUCCESS)
				addNotification(notif)
				fetchAllTags()
			})
			.catch((err) => {
				const msgText = tagPublicId
					? $t('UI.tags.notification.edit.fail')
					: $t('UI.tags.notification.create.fail')
				const notif = DefaultNotification(msgText + ': ' + err.message, NotificationType.ERROR)
				addNotification(notif)
			})
	}

	function deleteTag(publicId: string) {
		fetchUtils
			.delete('/api/tags/' + publicId)
			.then(() => {
				const notif = DefaultNotification(
					$t('UI.tags.notification.delete.success'),
					NotificationType.SUCCESS
				)
				addNotification(notif)
				fetchAllTags()
			})
			.catch((err) => {
				const notif = DefaultNotification(
					$t('UI.tags.notification.delete.fail') + ': ' + err.message,
					NotificationType.ERROR
				)
				addNotification(notif)
			})
	}

	onMount(fetchAllTags)
</script>

{#snippet deleteButton(tag: Tag)}
	<Button
		variant="secondary"
		color="red"
		disabled={tag.usageCount > 0}
		style="width: 32px; padding: 0; height: 32px;"
		onclick={() => openDeleteDialog(tag)}
	>
		<IconTrash width="24px" height="24px" />
	</Button>
{/snippet}

<Basic backgroundColor="var(--color-blueish-100)" maxWidth="1280px">
	<Permission requiredPermissions={UserPermission.MANAGE_GLOBAL_TAGS}>
		<EditTagDialog bind:this={editDialog} />
		<DeleteTagDialog bind:this={deleteDialog} />
		<div class="tags-conf__header">
			<h1>{$t('UI.tags.config.title')}</h1>
			<span>{$t('UI.tags.config.description')}</span>
		</div>

		<div class="tags-conf__table-container">
			<div class="tags-conf__table-header">
				<div class="tags-conf__table-header-left">
					<IconAnnotation fill="var(--primary-green)" width="24px" height="24px" />
					<h3>{$t('UI.tags.config.table.header')}</h3>
				</div>
				<div class="tags-conf__table-header-right">
					<InputField
						bind:value={searchTerm}
						placeholder={$t('UI.placeholder.search')}
						icon={InputIcons.SEARCH_LENS}
						containerWidth="min(100%, 255px)"
					/>
					<Button
						variant="primary"
						width="100%"
						style="width: 100%; max-width: 255px"
						onclick={() => openEditDialog()}
					>
						<span class="btn-text">{$t('UI.tags.config.button.create')}</span>
						<IconPlus />
					</Button>
				</div>
			</div>
			<table class="tags-conf__table">
				<thead>
					<tr>
						<th>{$t('UI.tags.config.table.col.tag')}</th>
						<th>{$t('UI.tags.config.table.col.usedIn')}</th>
						<th>{$t('UI.tags.config.table.col.actions')}</th>
					</tr>
				</thead>
				<tbody>
					{#each filteredTags as tag (tag.publicId)}
						<tr>
							<td>{tag.label}</td>
							<td>{`${tag.usageCount} ${$t('Lot' + (tag.usageCount === 1 ? '' : '.plural'))}`}</td>
							<td>
								<div class="tags-conf__actions">
									<Button
										variant="secondary"
										style="width: 32px; padding: 0; height: 32px;"
										onclick={() => openEditDialog(tag)}
										><IconEdit fill="#000" width="24px" height="24px" />
									</Button>

									{#if tag.usageCount > 0}
										<Tooltip_v5
											msg={$t('UI.tags.config.delete.tt.isInUse')}
											placementTtip={TooltipPlacements.TOP}
										>
											{@render deleteButton(tag)}
										</Tooltip_v5>
									{:else}
										{@render deleteButton(tag)}
									{/if}
								</div>
							</td>
						</tr>
					{:else}
						<tr>
							<td colspan="3" class="tags-conf__table-no-tags-row">
								<span>{$t('UI.tags.config.table.noTags')}</span>
							</td>
						</tr>
					{/each}
				</tbody>
			</table>
		</div>
	</Permission>
</Basic>

<style>
	.tags-conf__header {
		display: flex;
		max-width: 1080px;
		flex-direction: column;
		align-items: flex-start;
		gap: 12px;
		align-self: stretch;
		padding-bottom: 20px;

		& h1 {
			color: #000;
			font-family: Roboto, sans-serif;
			font-size: 30px;
			font-style: normal;
			font-weight: 500;
			line-height: 18px;
		}

		& span {
			color: #000;
			font-family: Roboto, sans-serif;
			font-size: 12px;
			font-style: normal;
			font-weight: 400;
			line-height: 18px;
		}
	}

	.tags-conf__table-container {
		display: flex;
		padding: 16px;
		flex-direction: column;
		align-items: flex-start;
		gap: 24px;
		align-self: stretch;
		border-radius: 6px;
		background: #fff;
	}

	.tags-conf__table-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		row-gap: 20px;
		align-self: stretch;

		.tags-conf__table-header-left {
			display: flex;
			align-items: center;
			gap: 10px;
			width: 100%;
		}

		.tags-conf__table-header-right {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			flex-wrap: wrap;
			gap: 12px;
			width: 100%;

			span.btn-text {
				color: #fff;
			}
		}

		h3 {
			margin: 0;
		}
	}

	.tags-conf__actions {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	table.tags-conf__table {
		width: 100%;
		border-collapse: collapse;
		border-spacing: 0;

		& > thead > tr > th:first-child,
		& > tbody > tr > td:first-child {
			padding-left: 48px;
		}

		& > thead > tr > th:last-child,
		& > tbody > tr > td:last-child {
			padding-right: 48px;
		}

		& > thead > tr {
			border-bottom: 1px solid var(--primary-green);
			height: 30px;
			color: #000;
			font-family: Roboto, sans-serif;
			font-size: 14px;
			font-style: normal;
			font-weight: 500;
			line-height: 18px;
			vertical-align: top;
		}

		& > tbody > tr {
			height: 32px;

			&:not(:last-child) {
				border-bottom: 1px solid var(--color-slate-300);
			}

			& > td {
				color: #000;
				font-family: Roboto, sans-serif;
				font-size: 14px;
				font-style: normal;
				font-weight: 400;
				line-height: normal;
				padding-top: 12px;
				padding-bottom: 12px;
			}
		}
	}

	.tags-conf__table-no-tags-row {
		text-align: center;

		& > span {
			font-family: Roboto, sans-serif;
			font-size: 14px;
			font-style: normal;
			font-weight: 400;
			color: var(--color-gray-600);
		}
	}

	/* Value taken from breakpoints.scss, but since we want to get rid of scss, just copied value xsmall. */
	@media (max-width: 576px) {
		.tags-conf__table-header {
			flex-direction: column;
		}

		table.tags-conf__table > thead > tr > th:first-child,
		table.tags-conf__table > tbody > tr > td:first-child {
			padding-left: 0;
		}

		table.tags-conf__table > thead > tr > th:last-child,
		table.tags-conf__table > tbody > tr > td:last-child {
			padding-right: 0;
		}
	}
</style>
